import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { languageTransfer } from "../../../utils/languageTransfer";
import i18next from "i18next";
import GameItem from "../../GameItem/GameItem";
import LoadingBlock from "../../Loading/LoadingBlock";
import NotFound from "../../ProgressBlock/NotFound";
import { Box, chakra, shouldForwardProp } from "@chakra-ui/react";
import { isValidMotionProp, motion } from "framer-motion";
import { gameListFromBottom } from "../../../animateConstant";
import "./styles.css";
const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

// 版面設定：一排 N 格、卡片比例、間距、面板底部預留高度
const COLS_PER_ROW = 6; // 每列格數
const CARD_ASPECT_RATIO = "9 / 16"; // 卡片寬高比（CSS aspect-ratio，上下長左右窄）
const ROW_GAP = 20; // 列與列間距 px
const COL_GAP = 12; // 同列各格間距 px
const BOTTOM_SPACE = 0; // 面板底部預留（避開底部選單）px

const GameSlideSelector = () => {
  const [searchParams] = useSearchParams();

  const searchParamsType = searchParams.get("type") || "Popular";

  const [isLoading, setIsLoading] = useState(true);

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const { newGameList, favoriteGameList, highlightGames, topGameList } =
    isHandleAllGame || {};

  const gameList = useMemo(() => {
    // 特別處理customSubType="New" 的情況
    if (searchParamsType === "New") {
      return (newGameList || []).slice(0, 10); // 直接返回 newGameList 只返回前8個 不需要其他過濾
    }

    // 特別處理customSubType="exclusiveBrandGames"的情況 顯示全部獨家遊戲
    if (searchParamsType === "favoriteGameList") {
      return favoriteGameList || []; // 直接返回全部獨家遊戲
    }

    // 特別處理customSubType="Popular"的情況 從highlightGames取前40個
    else {
      // 熱門依後端 topGameList 順序顯示，不做任何前端排序
      const gamesArray = topGameList || [];
      const sortedGames = gamesArray;

      return sortedGames.slice(0, 40);
    }
  }, [searchParamsType, newGameList, favoriteGameList, topGameList]);

  // 面板內部捲動需要「明確高度」：量測面板距視窗頂端距離，算出可用高度。
  // 依賴 isLoading：LoadingBlock 載入中不渲染子內容，等載入完成掛上 DOM 後才量得到。
  const scrollRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return; // 尚在載入中，子內容還沒掛載
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      const avail = window.innerHeight - top - BOTTOM_SPACE;
      setPanelHeight(Math.max(avail, 200)); // 至少 200px，避免算出負值
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isLoading]);

  useEffect(() => {
    setIsLoading(true);
  }, [searchParamsType]);

  return (
    <>
      <LoadingBlock size="50px" commonLoading={isLoading}>
        {gameList?.length === 0 && <NotFound />}
        <ChakraBox
          key={searchParamsType}
          {...gameListFromBottom}
          position="relative"
          h="100%"
        >
          {/* 捲動區：CSS grid（一排 COLS_PER_ROW 格）＋ 原生 overflow 下拉 */}
          <Box
            ref={scrollRef}
            // 有量到高度就用精準值，還沒量到先用 70vh 後備，確保一定有 overflow 可捲
            h={panelHeight ? `${panelHeight}px` : "70vh"}
            overflowY="auto"
            overflowX="hidden"
            p={`${ROW_GAP}px`}
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${COLS_PER_ROW}, 1fr)`,
              gap: `${ROW_GAP}px ${COL_GAP}px`,
              alignContent: "start", // 列由上往下排，不足一列靠上
              justifyItems: "start", // 不足一列的格子靠左
              // 頂端漸漸淡出，避免捲動時硬切
              maskImage:
                "linear-gradient(to bottom, transparent 0, #000 40px)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0, #000 40px)",
            }}
          >
            {gameList?.map((item, index) => {
              const gameName =
                item?.[
                  `gameName${languageTransfer({
                    lang: i18next.language,
                  })}`
                ];
              const gameNameEn = item?.gameNameEn;
              const platform = item?.game_platform;
              const gameSubType = item?.game_subtype;

              const gameCode = item?.gameCode;
              const gameType = item?.gameType;

              return (
                <Box
                  key={gameCode || index}
                  w="100%"
                  sx={{ aspectRatio: CARD_ASPECT_RATIO }} // 卡片維持設定比例
                >
                  <GameItem
                    gameProps={{
                      gameName,
                      platform,
                      gameCode,
                      gameType,
                      gameNameEn,
                      gameSubType,
                    }}
                    originProps={item}
                    itemIndex={index}
                    isNormalImage
                    longLayout
                    machineLayout
                  />
                </Box>
              );
            })}
          </Box>
        </ChakraBox>
      </LoadingBlock>
    </>
  );
};

export default GameSlideSelector;
